import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { uploadGalleryImage } from "../../store/slices/gallerySlice";
import { GALLERY_CATEGORIES } from "../../utils/constants";

const AdminGalleryUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.gallery);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    altText: "",
    tags: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!imageFile) {
      setSubmitError("Please select an image to upload.");
      return;
    }

    const fd = new FormData();
    fd.append("image", imageFile);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("category", form.category);
    fd.append("altText", form.altText || form.title);
    if (form.tags) fd.append("tags", form.tags);

    setSubmitting(true);
    try {
      await dispatch(uploadGalleryImage(fd)).unwrap();
      navigate("/admin/gallery");
    } catch (err) {
      setSubmitError(err || "Failed to upload image.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Upload Gallery Image - Admin</title>
      </Helmet>

      <div className="space-y-5">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/gallery")} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <FaArrowLeft size={14} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Upload Gallery Image</h1>
            <p className="text-gray-500 text-sm">Add a new image to the gallery</p>
          </div>
        </div>

        <div className="max-w-2xl">
          {submitError && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">{submitError}</div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8 space-y-6">
            {/* Image Upload */}
            <div>
              <label className="form-label">Image <span className="text-red-500">*</span></label>
              <label className="mt-2 cursor-pointer block">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-64 object-cover rounded-lg border mb-3" />
                ) : (
                  <div className="w-full h-64 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-blue-500 transition">
                    <FaUpload className="text-gray-400 text-4xl mb-3" />
                    <span className="text-gray-500 font-medium">Click to select image</span>
                    <span className="text-gray-400 text-sm mt-1">JPEG, PNG, WEBP up to 5MB</span>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
              {imagePreview && (
                <label className="cursor-pointer text-blue-600 text-sm hover:underline">
                  Change image
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="form-label">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="e.g. Manufacturing Floor"
              />
            </div>

            {/* Category */}
            <div>
              <label className="form-label">Category <span className="text-red-500">*</span></label>
              <select name="category" value={form.category} onChange={handleChange} required className="form-select">
                <option value="">Select Category</option>
                {GALLERY_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                className="form-textarea"
                placeholder="Optional description of the image"
              />
            </div>

            {/* Alt Text */}
            <div>
              <label className="form-label">Alt Text</label>
              <input
                type="text"
                name="altText"
                value={form.altText}
                onChange={handleChange}
                className="form-input"
                placeholder="Defaults to title if left empty"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="form-label">Tags</label>
              <input
                type="text"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. factory, production, quality (comma-separated)"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={submitting || loading}
                className="btn btn-primary px-8"
              >
                {submitting ? "Uploading..." : "Upload Image"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/gallery")}
                className="btn btn-secondary px-8"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AdminGalleryUpload;
